import { useFetcher } from '@remix-run/react';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { COPY, letterOf, ROLES, SHAPES, USES, type OwnQuestion, type RoleId, type ShapeId, type UseId } from '~/deck2/case/config';
import { catchCard, liftCard, useMakeRoom, type CaughtCard } from '~/deck2/case/motion';
import { OwnWords } from '~/deck2/case/OwnWords';
import { QuestionScreen } from '~/deck2/case/QuestionScreen';
import { ScreenSwap, type Direction } from '~/deck2/case/ScreenSwap';
import { Chip, Dots, KeyHint, Mark, theme, useHotkeys } from '~/deck2/ui';
import caseStyles from '~/deck2/case/case.module.css';
import { Arrow } from './Arrow';
import { start } from './config';
import { LaunchStep, silhouetteFor } from './LaunchStep';
import styles from './landing.module.css';
import { WorkStep, type Company } from './WorkStep';

export type StartActionData = { ok: true } | { ok: false; error: string };

const STEPS = ['about', 'use', 'work', 'role', 'email', 'done'] as const;
type Step = (typeof STEPS)[number];

/** Where Backspace goes from each step. In a field it deletes text instead, as it should. */
const BACK: Partial<Record<Step, Step>> = { use: 'about', work: 'use', role: 'work', email: 'role' };

/** Each answer, and the person's own words when it was said with "Something else". */
interface Picks {
  shape: { id: ShapeId; words?: string } | null;
  use: { id: UseId; words?: string } | null;
  work: Company | null;
  role: { id: RoleId; words?: string } | null;
}

/** A picked card on its way to becoming its chip at the top. */
interface Lift {
  card: CaughtCard;
  chip: 'shape' | 'use' | 'role';
}

/** Their own words in the reminder, cut at a word once they run long. */
const MAX_NAME = 64;
const shorten = (words: string) =>
  words.length <= MAX_NAME ? words : `${words.slice(0, words.lastIndexOf(' ', MAX_NAME) > 0 ? words.lastIndexOf(' ', MAX_NAME) : MAX_NAME)}…`;

/**
 * "Get started" before Deck is out: the product's own questions, asked the way the app asks them,
 * then where they work, their role and an email, then the day they get in. The answers go to the route's
 * action.
 */
export function StartFlow({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('about');
  const [direction, setDirection] = useState<Direction>('forward');
  const [picks, setPicks] = useState<Picks>({ shape: null, use: null, work: null, role: null });
  // The company found while they type, whose colour the page takes on.
  const [brand, setBrand] = useState<Company | null>(null);
  const [lift, setLift] = useState<Lift | null>(null);
  // "Something else", while its box is open: the card it grows out of, on which question.
  const [own, setOwn] = useState<{ from: DOMRect; question: OwnQuestion } | null>(null);
  const fetcher = useFetcher<StartActionData>();
  const emailInput = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const trail = useRef<HTMLElement>(null);
  useMakeRoom(trail);
  const sending = fetcher.state !== 'idle';
  const error = fetcher.data && !fetcher.data.ok ? fetcher.data.error : null;

  const goTo = (to: Step) => {
    setDirection(STEPS.indexOf(to) >= STEPS.indexOf(step) ? 'forward' : 'back');
    setStep(to);
  };

  useHotkeys(
    {
      Escape: own ? undefined : onClose,
      Backspace: BACK[step] ? () => goTo(BACK[step] as Step) : undefined,
    },
    !own,
  );

  // The page under the flow stays where it was and does not scroll behind it.
  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  // On a phone the answers so far can wrap onto a second row; the question starts below them (`--trail-bottom`).
  useLayoutEffect(() => {
    const row = trail.current;
    const page = root.current;
    if (!page) return undefined;
    if (!row) {
      page.style.removeProperty('--trail-bottom');
      return undefined;
    }
    const measure = () => page.style.setProperty('--trail-bottom', `${row.getBoundingClientRect().bottom}px`);
    measure();
    const resize = new ResizeObserver(measure);
    resize.observe(row);
    return () => resize.disconnect();
  }, [step]);

  // A new step opens at its top, even if the last one was scrolled to reach a card further down.
  useLayoutEffect(() => {
    root.current?.scrollTo({ top: 0 });
  }, [step]);

  // Once its chip is on screen (the step changes a render after the pick), the picked card turns into it.
  useLayoutEffect(() => {
    if (!lift) return;
    const chip = trail.current?.querySelector<HTMLElement>(`[data-glide="${lift.chip}"]`);
    if (chip) liftCard(lift.card, chip);
    setLift(null);
  }, [lift, step]);

  // The field is the only thing on the email screen; it takes the keys as it arrives.
  useEffect(() => {
    if (step === 'email') emailInput.current?.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.ok) {
      setDirection('forward');
      setStep('done');
    }
  }, [fetcher.state, fetcher.data]);

  const lifted = (title: HTMLElement, chip: Lift['chip']) => {
    const card = title.closest('button');
    if (card) setLift({ card: catchCard(card), chip });
  };

  /** "Something else" opens its box over the cards instead of answering. */
  const openOwn = (question: OwnQuestion, title: HTMLElement) =>
    setOwn({ from: title.closest('button')?.getBoundingClientRect() ?? title.getBoundingClientRect(), question });

  const pickShape = (id: ShapeId, title: HTMLElement) => {
    const answer = SHAPES.find((s) => s.id === id);
    if (answer && 'ownWords' in answer) return openOwn('about', title);
    lifted(title, 'shape');
    setPicks((current) => ({ ...current, shape: { id } }));
    goTo('use');
  };

  const pickUse = (id: UseId, title: HTMLElement) => {
    const answer = USES.find((u) => u.id === id);
    if (answer && 'ownWords' in answer) return openOwn('use', title);
    lifted(title, 'use');
    setPicks((current) => ({ ...current, use: { id } }));
    goTo('work');
  };

  const pickRole = (id: RoleId, title: HTMLElement) => {
    const answer = ROLES.find((r) => r.id === id);
    if (answer && 'ownWords' in answer) return openOwn('role', title);
    lifted(title, 'role');
    setPicks((current) => ({ ...current, role: { id } }));
    goTo('email');
  };

  const pickWork = (company: Company) => {
    setPicks((current) => ({ ...current, work: company }));
    setBrand(null);
    goTo('role');
  };

  const sayInOwnWords = (words: string, sounds: string | null) => {
    const question = own?.question ?? 'about';
    setOwn(null);
    if (question === 'about') {
      setPicks((current) => ({ ...current, shape: { id: (sounds as ShapeId | null) ?? 'other', words } }));
      goTo('use');
    } else if (question === 'use') {
      setPicks((current) => ({ ...current, use: { id: (sounds as UseId | null) ?? 'elsewhere', words } }));
      goTo('work');
    } else {
      setPicks((current) => ({ ...current, role: { id: (sounds as RoleId | null) ?? 'other', words } }));
      goTo('email');
    }
  };

  const ownBox = (question: OwnQuestion, active: boolean) =>
    active && own?.question === question ? (
      <OwnWords
        question={question}
        answers={question === 'about' ? SHAPES : question === 'use' ? USES : ROLES}
        from={own.from}
        onClose={() => setOwn(null)}
        onSubmit={sayInOwnWords}
      />
    ) : undefined;

  const shapeIndex = picks.shape ? SHAPES.findIndex((s) => s.id === picks.shape?.id) : -1;
  const useIndex = picks.use ? USES.findIndex((u) => u.id === picks.use?.id) : -1;
  const shapeLabel = picks.shape?.words ?? (shapeIndex >= 0 ? SHAPES[shapeIndex].title : '');
  const useLabel = picks.use?.words ?? (useIndex >= 0 ? USES[useIndex].title : '');
  const roleIndex = picks.role ? ROLES.findIndex((r) => r.id === picks.role?.id) : -1;
  const roleLabel = picks.role?.words ?? (roleIndex >= 0 ? ROLES[roleIndex].title : '');

  // What they save in their AI, in their own answers' words.
  const lower = (text: string) => (/^[A-Z][a-z]/.test(text) ? text[0].toLowerCase() + text.slice(1) : text);
  const company = picks.work?.name ?? '';
  const who = picks.role?.words
    ? `my role is ${lower(picks.role.words)}${company && !/\bat\b/i.test(picks.role.words) ? ` at ${company}` : ''}`
    : `I'm a ${lower(roleLabel || 'designer')}${company ? ` at ${company}` : ''}`;
  const about = picks.shape?.words ? `"${shorten(picks.shape.words)}"` : start.about[picks.shape?.id ?? 'other'];
  const audience = picks.use?.words ? `for ${lower(shorten(picks.use.words))}` : picks.use ? start.uses[picks.use.id] : '';
  const memory = start.done.memory(who, audience ? `${about}, ${audience}` : about);
  // How far along the steps this one is: each answer's chip shows from the step after it.
  const at = STEPS.indexOf(step);

  const render = (shown: Step, active: boolean) => {
    switch (shown) {
      case 'about':
        return (
          <QuestionScreen
            title={COPY.about.title}
            answers={SHAPES}
            picked={picks.shape?.words ? 'other' : picks.shape?.id ?? null}
            onPick={pickShape}
            hint={active && own?.question === 'about' ? COPY.ownWords.hint : COPY.about.hint}
            active={active}
            over={ownBox('about', active)}
          />
        );
      case 'use':
        return (
          <QuestionScreen
            title={COPY.use.title}
            answers={USES}
            picked={picks.use?.words ? 'elsewhere' : picks.use?.id ?? null}
            onPick={pickUse}
            hint={active && own?.question === 'use' ? COPY.ownWords.hint : COPY.use.hint}
            active={active}
            over={ownBox('use', active)}
          />
        );
      case 'work':
        return <WorkStep active={active} initial={picks.work} onFound={setBrand} onDone={pickWork} />;
      case 'role':
        return (
          <QuestionScreen
            title={start.role.title}
            answers={ROLES}
            picked={picks.role?.words ? 'other' : picks.role?.id ?? null}
            onPick={pickRole}
            hint={active && own?.question === 'role' ? COPY.ownWords.hint : start.role.hint}
            active={active}
            over={ownBox('role', active)}
          />
        );
      case 'email':
        return (
          <section className={caseStyles.question}>
            <header className={caseStyles.ask}>
              <h1 className={`${caseStyles.title} ${styles.inviteTitle}`}>{start.email.title}</h1>
            </header>
            {/* Its own route, so it signs people up from `/` as well as from `/cases/landing`. */}
            <fetcher.Form method="post" action="/cases/landing" className={styles.pillForm}>
              <input type="hidden" name="shape" value={picks.shape?.id ?? ''} />
              <input type="hidden" name="use" value={picks.use?.id ?? ''} />
              <input type="hidden" name="company" value={picks.work?.name ?? ''} />
              <input type="hidden" name="role" value={picks.role?.id ?? ''} />
              {/* Bots fill every field; people never see this one. */}
              {/* A bot trap. Named so no password manager or browser fills it in for a real person. */}
              <input
                className={styles.honeypot}
                type="text"
                name="cs_trap"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden
                data-1p-ignore
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
              <div className={styles.pill}>
                <input
                  className={styles.pillInput}
                  type="email"
                  name="email"
                  autoComplete="email"
                  aria-label={start.email.label}
                  placeholder={start.email.placeholder(picks.work?.domain ?? null)}
                  required
                  maxLength={320}
                  disabled={!active}
                  ref={active ? emailInput : undefined}
                />
                <button type="submit" className={`${styles.button} ${styles.ink} ${styles.pillSubmit}`} disabled={sending || !active} aria-busy={sending || undefined}>
                  {start.email.submit}
                  {sending ? <Dots label="Saving" /> : <Arrow />}
                </button>
              </div>
              {error ? <p className={styles.emailError}>{error}</p> : null}
            </fetcher.Form>
            <KeyHint parts={start.email.hint} />
          </section>
        );
      case 'done':
        return <LaunchStep message={memory} />;
    }
  };

  return (
    <div ref={root} className={`${theme.theme} ${styles.start}`} role="dialog" aria-modal="true" aria-label={COPY.about.title}>
      <div className={styles.startTheme} data-motion-layer>
        {/* Where they work, as they type it: the page takes on their company's colour. */}
        <div
          className={`${styles.glow} ${step === 'work' && brand?.colour ? styles.glowOn : ''}`}
          style={brand?.colour ? ({ '--brand': brand.colour } as CSSProperties) : undefined}
          aria-hidden
        />
        {/* The last screen's ground: a brand silhouette in the colour nearest their company's. */}
        {step === 'done' ? (
          <div className={styles.launchGround} style={{ backgroundImage: `url(${silhouetteFor(picks.work?.colour ?? null)})` }} aria-hidden />
        ) : null}
        <Mark size={26} className={styles.startMark} />
        <button type="button" className={styles.startClose} onClick={onClose} aria-label={start.close}>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
            <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        {at > 0 && step !== 'done' ? (
          <nav ref={trail} className={caseStyles.trail} aria-label="Your answers">
            {picks.shape ? <Chip shortcut={letterOf(shapeIndex)} label={shapeLabel} labelId="shape" onClick={() => goTo('about')} /> : null}
            {picks.use && at > 1 ? <Chip shortcut={letterOf(useIndex)} label={useLabel} labelId="use" onClick={() => goTo('use')} /> : null}
            {picks.work && at > 2 ? <Chip icon={picks.work.logo} label={picks.work.name} labelId="work" onClick={() => goTo('work')} /> : null}
            {picks.role && at > 3 ? <Chip shortcut={letterOf(roleIndex)} label={roleLabel} labelId="role" onClick={() => goTo('role')} /> : null}
          </nav>
        ) : null}
        <ScreenSwap step={step} direction={direction} render={render} />
      </div>
    </div>
  );
}
